import {
  Autocomplete,
  Button,
  TextField,
  Typography,
  LinearProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "../axios";
import { useMutation, useQuery } from "react-query";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { queryClient } from "../index";

const EditClassForm = ({ classData, teachers, currentTeacher }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { handleSubmit, control } = useForm({
    defaultValues: {
      name: classData.name,
      fee: classData.fee,
      teacher: currentTeacher,
    },
  });

  const updateClassMutation = useMutation((data) =>
    axios.put(`/classes/${id}`, data)
  );

  const handleFormSubmission = (data) => {
    updateClassMutation.mutate(
      {
        name: data.name,
        fee: data.fee,
        teacherID: data.teacher ? data.teacher.id : null,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["class", id]);
          queryClient.invalidateQueries("classes");
          toast.success("Class updated successfully");
          navigate("/classes");
        },
        onError: () => {
          toast.error("Error updating class");
        },
      }
    );
  };

  return (
    <div>
      <Typography mb={2} variant="h5" component="h2">
        Edit Class: {classData.name}
      </Typography>
      <Box
        onSubmit={handleSubmit(handleFormSubmission)}
        component="form"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 2,
        }}
        autoComplete="off"
      >
        <Controller
          render={({ field }) => (
            <TextField {...field} label="Class name" required />
          )}
          control={control}
          name="name"
        />
        <Controller
          render={({ field }) => (
            <TextField {...field} label="Fee" type="number" />
          )}
          control={control}
          name="fee"
        />
        <Controller
          render={({ field }) => (
            <Autocomplete
              options={teachers}
              getOptionLabel={(option) => option.name}
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Assign Teacher" />
              )}
            />
          )}
          control={control}
          name="teacher"
        />
        <div></div>
        <Button type="submit" variant="contained">
          Update
        </Button>
      </Box>

      {classData.students && classData.students.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" mb={1}>
            Students in this class ({classData.students.length})
          </Typography>
          {classData.students.map((s) => (
            <Typography key={s.id} variant="body2">
              {s.fullName}
            </Typography>
          ))}
        </Box>
      )}
    </div>
  );
};

const EditClass = () => {
  const { id } = useParams();

  const { data: classData, isLoading: classLoading } = useQuery(
    ["class", id],
    () => axios.get(`/classes/${id}`).then((res) => res.data)
  );

  const { data: teachers, isLoading: teachersLoading } = useQuery(
    "teachers",
    () => axios.get("/teachers").then((res) => res.data)
  );

  if (classLoading || teachersLoading) return <LinearProgress />;
  if (!classData) return <Typography>Class not found</Typography>;

  const currentTeacher = classData.teacher
    ? teachers.find((t) => t.id === classData.teacher.id) || null
    : null;

  return (
    <EditClassForm
      classData={classData}
      teachers={teachers}
      currentTeacher={currentTeacher}
    />
  );
};

export default EditClass;
